import { useEffect, useMemo, useRef } from 'react';
import { History, Plus, Square } from 'lucide-react';
import {
  buildLayerIndexPayload,
  buildSelectionPayload,
  buildToolDefs,
  buildViewStatePayload,
} from '@/agent/dispatcher-bridge';
import type { ChatMessage as StoredChatMessage } from './chatStore';
import { STREAMING_MESSAGE_ID, useChatStore } from './chatStore';
import { ChatComposer } from './ChatComposer';
import { MessageBubble } from './MessageBubble';
import { ActivityPanel } from './ActivityPanel';
import { useAgentActivityStore } from '@/state/agentActivityStore';
import { tryHandleSlashCommand } from './slash-commands';
import type { HistoryEntry } from '../../../shared/ipc';

const HISTORY_PAIR_CAP = 6;

export function ChatPanel() {
  const conversations = useChatStore((s) => s.conversations);
  const currentConversationId = useChatStore((s) => s.currentConversationId);
  const sending = useChatStore((s) => s.sending);
  const model = useChatStore((s) => s.model);
  const models = useChatStore((s) => s.models);
  const agentReady = useChatStore((s) => s.agentReady);
  const agentError = useChatStore((s) => s.agentError);
  const setAgentConfig = useChatStore((s) => s.setAgentConfig);
  const setAgentError = useChatStore((s) => s.setAgentError);
  const setModel = useChatStore((s) => s.setModel);
  const setCurrentConversation = useChatStore((s) => s.setCurrentConversation);
  const startNewChat = useChatStore((s) => s.startNewChat);
  const pruneExpiredConversations = useChatStore((s) => s.pruneExpiredConversations);
  const pushMessage = useChatStore((s) => s.pushMessage);
  const beginStreamingAssistant = useChatStore((s) => s.beginStreamingAssistant);
  const appendStreamingDelta = useChatStore((s) => s.appendStreamingDelta);
  const finalizeStreamingMessage = useChatStore((s) => s.finalizeStreamingMessage);
  const discardStreamingMessage = useChatStore((s) => s.discardStreamingMessage);
  const setSending = useChatStore((s) => s.setSending);
  const pendingActivity = useAgentActivityStore((s) => s.pending);

  const scrollRef = useRef<HTMLDivElement>(null);
  const stopRequestedRef = useRef(false);
  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === currentConversationId) ?? null,
    [conversations, currentConversationId],
  );
  const messages = activeConversation?.messages ?? [];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await window.api.getAgentConfig();
        if (!cancelled) setAgentConfig(cfg);
      } catch (err) {
        if (!cancelled) setAgentError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setAgentConfig, setAgentError]);

  useEffect(() => {
    pruneExpiredConversations();
  }, [pruneExpiredConversations]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, sending]);

  // Subscribe once to chunk events. The streaming message in the store grows
  // as text deltas arrive; nothing else here cares about which conversation
  // is current — appendStreamingDelta is keyed off the active conversation.
  useEffect(() => {
    const off = window.api.onChatChunk((chunk) => {
      if (chunk.type === 'text') {
        appendStreamingDelta(chunk.delta);
      }
    });
    return off;
  }, [appendStreamingDelta]);

  const onSubmit = async (text: string) => {
    pushMessage({ role: 'user', content: text });
    const slash = await tryHandleSlashCommand(text);
    if (slash.handled) {
      pushMessage({ role: 'system', content: slash.reply });
      return;
    }
    if (!agentReady || !model) {
      pushMessage({
        role: 'system',
        content: agentError ? `Agent unavailable: ${agentError}` : 'Agent not ready yet.',
      });
      return;
    }

    stopRequestedRef.current = false;
    setSending(true);
    beginStreamingAssistant();

    try {
      const state = useChatStore.getState();
      const conv = state.conversations.find((c) => c.id === state.currentConversationId);
      const priorMessages = (conv?.messages ?? []).filter(
        (m) =>
          m.role !== 'system' &&
          m.id !== STREAMING_MESSAGE_ID &&
          // The latest user message is what we just pushed; it goes in `message`,
          // not `history`.
          !(m.role === 'user' && m.content === text),
      );
      const history = buildHistoryFromMessages(priorMessages);

      const response = await window.api.chat({
        message: text,
        history,
        model,
        selection: buildSelectionPayload(),
        layer_index: buildLayerIndexPayload(),
        tools: buildToolDefs(),
        view_state: buildViewStatePayload(),
      });

      if (stopRequestedRef.current) {
        discardStreamingMessage();
        useAgentActivityStore.getState().clearPending();
        pushMessage({ role: 'system', content: 'Chat stopped.' });
      } else {
        finalizeStreamingMessage({
          content: response.text || '*(empty reply)*',
          toolRounds: response.rounds,
        });
        // Pull the just-finalized assistant message id off the store so we
        // can attach the in-flight activity to it permanently.
        const after = useChatStore.getState();
        const conv = after.conversations.find((c) => c.id === after.currentConversationId);
        const last = conv?.messages[conv.messages.length - 1];
        if (last && last.role === 'assistant') {
          useAgentActivityStore.getState().commitPendingTo(last.id);
        } else {
          useAgentActivityStore.getState().clearPending();
        }
      }
    } catch (err) {
      discardStreamingMessage();
      useAgentActivityStore.getState().clearPending();
      if (stopRequestedRef.current) {
        pushMessage({ role: 'system', content: 'Chat stopped.' });
      } else {
        pushMessage({
          role: 'system',
          content: `Error: ${(err as Error).message ?? 'unknown'}`,
        });
      }
    } finally {
      stopRequestedRef.current = false;
      setSending(false);
    }
  };

  const onStop = () => {
    if (!sending) return;
    stopRequestedRef.current = true;
    window.api.chatAbort();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-card/50 px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-xs uppercase tracking-wide text-muted-foreground">Chat</div>
          <div className="truncate text-sm font-semibold">{activeConversation?.title ?? 'New chat'}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            className="inline-flex h-8 items-center gap-1 rounded border border-border px-2 text-xs hover:bg-accent"
            onClick={() => startNewChat()}
            title="Start a new chat"
          >
            <Plus className="size-3.5" />
            New
          </button>
          {conversations.length > 1 && (
            <label className="relative inline-flex h-8 items-center rounded border border-border px-1 hover:bg-accent">
              <History className="pointer-events-none ml-1 mr-1 size-3.5 text-muted-foreground" />
              <select
                className="h-full max-w-[150px] appearance-none bg-transparent pr-2 text-xs outline-none"
                value={currentConversationId ?? ''}
                onChange={(e) => setCurrentConversation(e.target.value)}
                title="Open recent chat"
              >
                {conversations.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          {models.length > 0 && (
            <select
              className="h-8 rounded border border-border bg-background px-2 text-xs"
              value={model ?? ''}
              onChange={(e) => setModel(e.target.value)}
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-3">
        {messages.length === 0 && (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-3 text-xs text-muted-foreground">
            <p className="mb-2 text-sm font-medium text-foreground">Start a fresh conversation</p>
            <p>
              Ask anything about map layers, selections, and analysis. Use the history menu to reopen
              previous chats from the last 7 days.
            </p>
            <p className="mt-2">
              Type <code className="rounded bg-muted px-1">/help</code> to view slash commands.
            </p>
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
        {sending && pendingActivity.length > 0 && (
          <ActivityPanel entries={pendingActivity} />
        )}
        {sending && (
          <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/20 px-2.5 py-1.5">
            <div className="text-xs italic text-muted-foreground">Thinking…</div>
            <button
              type="button"
              onClick={onStop}
              className="inline-flex h-6 items-center gap-1 rounded border border-border bg-background px-2 text-[11px] font-medium hover:bg-accent"
              title="Stop the agent"
            >
              <Square className="size-3 fill-current" />
              Stop
            </button>
          </div>
        )}
      </div>

      <ChatComposer onSubmit={onSubmit} disabled={sending} />
    </div>
  );
}

// Build the history we send to Anthropic from the persisted message log.
// Keeps the LAST `HISTORY_PAIR_CAP` (user, assistant) pairs and inlines any
// tool rounds attached to those assistant messages so handles like h_3 stay
// alive across user turns. Older turns are dropped entirely (text + rounds);
// the title summary on the conversation card is enough for the user.
function buildHistoryFromMessages(messages: StoredChatMessage[]): HistoryEntry[] {
  // Walk pairs from the end so we can stop once we have enough. Each pair is
  // (assistant, immediately-preceding user). System messages are already
  // filtered upstream.
  const pairs: { user: StoredChatMessage; assistant: StoredChatMessage }[] = [];
  for (let i = messages.length - 1; i >= 0 && pairs.length < HISTORY_PAIR_CAP; i--) {
    const m = messages[i];
    if (m.role !== 'assistant') continue;
    const prev = messages[i - 1];
    if (!prev || prev.role !== 'user') continue;
    pairs.unshift({ user: prev, assistant: m });
    i -= 1;
  }

  const out: HistoryEntry[] = [];
  for (const { user, assistant } of pairs) {
    out.push({ kind: 'text', role: 'user', content: user.content });
    if (assistant.toolRounds) {
      for (const round of assistant.toolRounds) {
        out.push({ kind: 'tool_round', round });
      }
    }
    out.push({ kind: 'text', role: 'assistant', content: assistant.content });
  }
  return out;
}
